/**
 * Fetch-based API client that speaks the tabgraph REST + protojson wire format.
 *
 * API base URL is configurable via VITE_API_BASE environment variable.
 * Defaults to https://api.tabgraph.stratusstudios.com in production,
 * http://localhost:8080 in development.
 */

import type { Graph } from "@/gen/tabgraph/v1/graph_pb";
import { GraphSchema } from "@/gen/tabgraph/v1/graph_pb";
import type { Session, SessionSummary } from "@/gen/tabgraph/v1/session_pb";
import { SessionSchema, SessionSummarySchema } from "@/gen/tabgraph/v1/session_pb";
import type { User } from "@/gen/tabgraph/v1/user_pb";
import { UserSchema } from "@/gen/tabgraph/v1/user_pb";
import type { Device } from "@/gen/tabgraph/v1/device_pb";
import { DeviceSchema } from "@/gen/tabgraph/v1/device_pb";
import type { ShareToken } from "@/gen/tabgraph/v1/share_pb";
import { ShareTokenSchema } from "@/gen/tabgraph/v1/share_pb";
import type { SearchHit } from "@/gen/tabgraph/v1/services/search_service_pb";
import { SearchHitSchema } from "@/gen/tabgraph/v1/services/search_service_pb";
import { fromProtoJson } from "./codec";
import { getAccessToken, refreshAccessToken, clearAccessToken, signOut } from "./auth";
import type { JsonValue } from "@bufbuild/protobuf";

export const API_BASE =
  import.meta.env.VITE_API_BASE ??
  (import.meta.env.DEV
    ? "http://localhost:8080"
    : "https://api.tabgraph.stratusstudios.com");

export interface Pagination {
  limit?: number;
  cursor?: string;
}

export interface PaginatedResult<T> {
  items: T[];
  nextCursor: string;
  hasMore: boolean;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(`${status}: ${message}`);
    this.name = "ApiError";
  }
}

async function fetchWithAuth(
  url: string,
  init: RequestInit = {},
  retried = false,
): Promise<Response> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string>),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(url, { ...init, headers, credentials: "include" });

  if (res.status === 401 && !retried) {
    // Attempt token refresh and retry once
    const newToken = await refreshAccessToken(API_BASE);
    if (newToken) {
      return fetchWithAuth(url, init, true);
    }
    clearAccessToken();
    throw new ApiError(401, "Unauthorized");
  }

  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text);
  }

  return res;
}

// ---- Auth ----

export async function me(): Promise<User> {
  const res = await fetchWithAuth(`${API_BASE}/v1/me`);
  // The response shape is { user: {...} }
  const raw = (await res.json()) as { user?: JsonValue };
  if (!raw.user) throw new ApiError(500, "Missing user in response");
  return fromProtoJson(UserSchema, raw.user);
}

// ---- Devices ----

export async function listDevices(): Promise<Device[]> {
  const res = await fetchWithAuth(`${API_BASE}/v1/devices`);
  const body = (await res.json()) as { devices?: JsonValue[] };
  return (body.devices ?? []).map((d) => fromProtoJson(DeviceSchema, d));
}

// ---- Sessions ----

export async function listSessions(
  pagination?: Pagination,
): Promise<PaginatedResult<SessionSummary>> {
  const params = new URLSearchParams();
  if (pagination?.limit) params.set("limit", String(pagination.limit));
  if (pagination?.cursor) params.set("cursor", pagination.cursor);

  const res = await fetchWithAuth(
    `${API_BASE}/v1/sessions?${params.toString()}`,
  );
  const body = (await res.json()) as {
    sessions?: JsonValue[];
    pagination?: { nextCursor?: string; hasMore?: boolean };
  };

  const sessions = (body.sessions ?? []).map((s) =>
    fromProtoJson(SessionSummarySchema, s),
  );
  return {
    items: sessions,
    nextCursor: body.pagination?.nextCursor ?? "",
    hasMore: body.pagination?.hasMore ?? false,
  };
}

export async function getSession(id: string): Promise<Graph> {
  const res = await fetchWithAuth(`${API_BASE}/v1/sessions/${id}`);
  const body = (await res.json()) as { graph?: JsonValue };
  if (!body.graph) throw new ApiError(500, "Missing graph in response");
  return fromProtoJson(GraphSchema, body.graph);
}

export async function saveSession(
  id: string,
  name?: string,
): Promise<Session> {
  const payload: Record<string, unknown> = { id };
  if (name !== undefined) payload.name = name;

  const res = await fetchWithAuth(`${API_BASE}/v1/sessions/${id}:save`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const body = (await res.json()) as { session?: JsonValue };
  if (!body.session) throw new ApiError(500, "Missing session in response");
  return fromProtoJson(SessionSchema, body.session);
}

export async function endSession(id: string): Promise<Session> {
  const res = await fetchWithAuth(`${API_BASE}/v1/sessions/${id}:end`, {
    method: "POST",
    body: JSON.stringify({ id }),
  });
  const body = (await res.json()) as { session?: JsonValue };
  if (!body.session) throw new ApiError(500, "Missing session in response");
  return fromProtoJson(SessionSchema, body.session);
}

export async function renameSession(
  id: string,
  name?: string,
  description?: string,
): Promise<Session> {
  const payload: Record<string, unknown> = { id };
  if (name !== undefined) payload.name = name;
  if (description !== undefined) payload.description = description;

  const res = await fetchWithAuth(`${API_BASE}/v1/sessions/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  const body = (await res.json()) as { session?: JsonValue };
  if (!body.session) throw new ApiError(500, "Missing session in response");
  return fromProtoJson(SessionSchema, body.session);
}

export async function deleteSession(id: string): Promise<void> {
  await fetchWithAuth(`${API_BASE}/v1/sessions/${id}`, {
    method: "DELETE",
  });
}

// ---- Search ----

export async function searchNodes(
  q: string,
  pagination?: Pagination,
): Promise<PaginatedResult<SearchHit>> {
  const params = new URLSearchParams({ query: q });
  if (pagination?.limit) params.set("limit", String(pagination.limit));
  if (pagination?.cursor) params.set("cursor", pagination.cursor);

  const res = await fetchWithAuth(
    `${API_BASE}/v1/search/nodes?${params.toString()}`,
  );
  const body = (await res.json()) as {
    hits?: JsonValue[];
    pagination?: { nextCursor?: string; hasMore?: boolean };
  };

  const hits = (body.hits ?? []).map((h) =>
    fromProtoJson(SearchHitSchema, h),
  );
  return {
    items: hits,
    nextCursor: body.pagination?.nextCursor ?? "",
    hasMore: body.pagination?.hasMore ?? false,
  };
}

// ---- Share ----

export async function createShareLink(
  sessionId: string,
  ttlHours?: number,
): Promise<{ shareToken: ShareToken; rawToken: string }> {
  const res = await fetchWithAuth(`${API_BASE}/v1/sessions/${sessionId}/share`, {
    method: "POST",
    body: JSON.stringify({ sessionId, ttlHours: ttlHours ?? 0 }),
  });
  const body = (await res.json()) as {
    shareToken?: JsonValue;
    rawToken?: string;
  };
  if (!body.shareToken || !body.rawToken)
    throw new ApiError(500, "Missing share token in response");
  return {
    shareToken: fromProtoJson(ShareTokenSchema, body.shareToken),
    rawToken: body.rawToken,
  };
}

export async function revokeShareLink(tokenId: string): Promise<void> {
  await fetchWithAuth(`${API_BASE}/v1/share/${tokenId}`, {
    method: "DELETE",
  });
}

export async function getSharedGraph(token: string): Promise<Graph> {
  // Anonymous — no auth header
  const res = await fetch(`${API_BASE}/v1/share/${token}`);
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text);
  }
  const body = (await res.json()) as { graph?: JsonValue };
  if (!body.graph) throw new ApiError(500, "Missing graph in response");
  return fromProtoJson(GraphSchema, body.graph);
}

// Re-export sign-out so callers can keep their single import from api.ts.
// Sign-in is now redirect-based (see lib/oidc.ts), so it isn't a function
// on the api surface anymore.
export { signOut };
